import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Separator } from "@repo/ui";

const projects = [
  {
    title: "Neon Liner Landing",
    image: "https://images.pexels.com/photos/6835448/pexels-photo-6835448.jpeg",
    desc: "Landing page concept for a neon eyeliner campaign with bold CTA and editorial layout.",
    href: "#",
    tags: ["React", "UI/UX", "Animation"],
  },
  {
    title: "Swipe-Proof Micro Site",
    image: "https://images.pexels.com/photos/9109105/pexels-photo-9109105.jpeg",
    desc: "Interactive micro site highlighting smudge tests and shade comparisons.",
    href: "#",
    tags: ["Hono", "shadcn", "Charts"],
  },
  {
    title: "Color Story Lookbook",
    image: "https://images.pexels.com/photos/15783138/pexels-photo-15783138.jpeg",
    desc: "Scroll story presenting eyeliner looks across seasons with pastel gradients.",
    href: "#",
    tags: ["TypeScript", "Design", "Accessibility"],
  },
];

export function Home() {
  return (
    <div className="min-h-dvh">
      {/* Hero */}
      <section className="px-6 md:px-10 pt-20 md:pt-28 pb-10">
        <div className="max-w-5xl mx-auto grid md:grid-cols-[1.2fr_0.8fr] items-center gap-10">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-sm">
              <span className="h-2 w-2 rounded-full bg-primary" />
              เปิดใจ-เปิดเส้นไลเนอร์ที่ใช่
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
              นายมะเดี่ยว
              <span className="block text-foreground/80 text-xl md:text-2xl font-medium mt-2">
                Frontend Developer & UI Designer for Beauty/Cosmetics
              </span>
            </h1>
            <p className="text-lg md:text-xl text-foreground/70 max-w-2xl">
              สร้างประสบการณ์เว็บสดใส อ่านง่าย และเข้าถึงได้
              ช่วยแบรนด์อายไลเนอร์เล่าเรื่องสีสันให้โดดเด่น ด้วยดีไซน์มินิมอลและแอนิเมชันเบาๆ
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild>
                <a href="#contact">ติดต่อฉัน</a>
              </Button>
              <Button variant="outline" asChild>
                <a href="#projects">ดูผลงาน</a>
              </Button>
              <Badge className="rounded-full" variant="secondary">
                พร้อมเริ่มงานทันที
              </Badge>
            </div>
          </div>
          <div>
            <Card className="overflow-hidden border-border/60">
              <img
                src="https://images.pexels.com/photos/7956290/pexels-photo-7956290.jpeg"
                alt="Playful eyeliner look"
                className="h-72 w-full object-cover md:h-[420px]"
              />
            </Card>
          </div>
        </div>
      </section>

      <Separator className="max-w-5xl mx-auto" />

      {/* About */}
      <section id="about" className="px-6 md:px-10 py-14">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-1">
            <h2 className="text-2xl md:text-3xl font-bold">About</h2>
            <p className="text-foreground/70 mt-2">ทำไมฉันเหมาะกับอายไลเนอร์</p>
          </div>
          <div className="md:col-span-2 space-y-4">
            <p className="text-foreground/80">
              ฉันโฟกัสที่เว็บที่รู้สึก “สดชื่น” เน้นการจัดวางที่โล่ง อ่านง่าย
              สีสันที่เป็นตัวตนของแบรนด์ และการเข้าถึงได้ตามมาตรฐาน ใช้ React + TypeScript + Hono
              และ shadcn-ui เพื่อสร้างส่วนติดต่อผู้ใช้ที่คมชัดและดูแพง
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">React</Badge>
              <Badge variant="secondary">TypeScript</Badge>
              <Badge variant="secondary">Hono</Badge>
              <Badge variant="secondary">shadcn</Badge>
              <Badge variant="secondary">Tailwind</Badge>
              <Badge variant="secondary">Accessibility</Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section id="projects" className="px-6 md:px-10 py-14">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">Works</h2>
              <p className="text-foreground/70">ตัวอย่างงานสายบิวตี้/เมคอัพ</p>
            </div>
            <Button variant="ghost" asChild>
              <a href="#contact">ร่วมงานกัน</a>
            </Button>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {projects.map((p) => (
              <Card key={p.title} className="overflow-hidden">
                <img src={p.image} alt={p.title} className="h-44 w-full object-cover" />
                <CardHeader>
                  <CardTitle className="text-lg">{p.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-foreground/70">{p.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {p.tags.map((t) => (
                      <Badge key={t} variant="outline" className="border-border/70">
                        {t}
                      </Badge>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" asChild>
                    <a href={p.href}>รายละเอียด</a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Skills */}
      <section id="skills" className="px-6 md:px-10 py-14">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-1">
            <h2 className="text-2xl md:text-3xl font-bold">Skills</h2>
            <p className="text-foreground/70 mt-2">สิ่งที่ทำได้ดี</p>
          </div>
          <div className="md:col-span-2">
            <div className="flex flex-wrap gap-2">
              {[
                "React",
                "TypeScript",
                "Hono",
                "Tailwind",
                "shadcn",
                "Radix",
                "Drizzle",
                "React Query",
                "SEO",
                "Perf",
                "i18n",
                "A11y",
              ].map((s) => (
                <Badge key={s} variant="secondary">
                  {s}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="px-6 md:px-10 py-14">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-1">
            <h2 className="text-2xl md:text-3xl font-bold">Contact</h2>
            <p className="text-foreground/70 mt-2">พร้อมคุยงานทันที</p>
          </div>
          <div className="md:col-span-2 space-y-4">
            <Card>
              <CardContent className="py-6">
                <div className="flex flex-wrap items-center gap-4">
                  <Badge variant="outline" className="border-primary text-primary">
                    Email
                  </Badge>
                  <a className="underline-offset-4 hover:underline" href="mailto:hello@example.com">
                    hello@example.com
                  </a>
                  <Separator orientation="vertical" className="hidden md:block h-6" />
                  <Badge variant="outline">LinkedIn</Badge>
                  <a className="underline-offset-4 hover:underline" href="#">
                    /naimadiew
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <footer className="px-6 md:px-10 pb-10">
        <div className="max-w-5xl mx-auto text-sm text-foreground/60">
          © {new Date().getFullYear()} นายมะเดี่ยว · แพสชันสายบิวตี้ & เว็บสดใส
        </div>
      </footer>
    </div>
  );
}
